import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import LLMService from '../services/llm.service';
import { formatCredits } from '../../common/tools/formatCredits';

/**
 * Account → Preferences block: the LLM that AI calls default to.
 *
 * Contributed to the account page through the shell registry's
 * `accountSections` slot, so the user module never imports the generator.
 *
 * The listing is availability-filtered server-side — a provider with no API key
 * or an exhausted account (tripped circuit breaker) is absent — so the choice
 * saved here is always one that can actually run. The AI confirmation modal
 * pre-selects it and still allows a different model per call.
 *
 * Each option carries its price relative to the platform default, because the
 * model pinned here is the one that will silently price every later call. A
 * user picking a default without seeing that a model costs three times another
 * finds out only on the bill.
 */
const AIModelPreference = () => {
  const { t } = useTranslation('generator');
  const [providers, setProviders] = useState([]);
  const [model, setModel]         = useState('');
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([LLMService.getModels(), LLMService.getPreference()])
      .then(([listing, preference]) => {
        if (cancelled) return;
        setProviders(listing?.providers || []);
        setModel(preference?.model || '');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const changeModel = async (chosen) => {
    if (saving) return;
    const previous = model;
    setModel(chosen);
    setSaving(true);
    const response = await LLMService.setPreference(chosen);
    setSaving(false);
    if (response?.detail?.[0]?.success) {
      toast.success(t(response.detail[0].success[0]));
    } else {
      setModel(previous);
      toast.error(t('llm-preference-save-failed'));
    }
  };

  if (!providers.length) return null;

  return (
    <div className="mt-4" data-testid="ai-model-preference">
      <h5>{t('ai-model-preference')}</h5>
      <p className="text-muted">{t('ai-model-preference-intro')}</p>
      <select
        id="default-ai-model"
        data-testid="default-ai-model"
        className="form-select"
        style={{ maxWidth: '360px' }}
        value={model}
        disabled={saving}
        onChange={(e) => changeModel(e.target.value)}
      >
        {/* Explicit "no pin" choice — follow whatever the platform default is,
            which keeps working when a pinned provider later runs dry. */}
        <option value="">{t('ai-model-platform-default')}</option>
        {providers.map((provider) => (
          <optgroup key={provider.provider} label={provider.label}>
            {(provider.models || []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
                {m.coefficient != null && m.coefficient !== 1 && (
                  ` — ${t('ai-model-relative-cost', {
                    factor: formatCredits(m.coefficient),
                  })}`
                )}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  );
};

export default AIModelPreference;
